<template>
  <section class="home-page">
    <!-- Home Section -->
    <ImageCarousel
      :slides="slides"
      title=""
      class="pa-0"
    />

    <WaveDivider
      :reactive="false"
      color="rgb(var(--v-theme-secondary))"
    />

    <!-- Looking for something extra special Section -->
    <v-container
      fluid
      class="py-16"
    >
      <v-row justify="center">
        <v-col
          cols="12"
          md="10"
          class="text-center"
        />
      </v-row>
    </v-container>

    <CompanyPhilosophy />
    <AboutUsSection />

    <!-- Culture Section -->
    <v-container
      id="culture"
      fluid
      class="py-16"
    >
      <v-row justify="center">
        <v-col
          cols="12"
          md="10"
          class="text-center"
        >
          <div class="app-fade-in-section">
            <h2 class="text-h4 text-sm-h3 text-md-h2 font-weight-bold mb-3 mb-md-4">
              More Than a Job, A Community.
            </h2>
            <p
              class="text-responsive mx-auto mb-16"
              style="max-width: 800px"
            >
              We're building a community, not just a company. We focus on
              placement on healthy, vetted teams—often with supportive teammates
              —and foster a culture of support through constant
              connection and peer mentorship.
            </p>
            <v-row>
              <v-col
                cols="12"
                md="4"
              >
                <v-card class="app-culture-card rounded-2xl">
                  <v-img
                    :src="coachesWide"
                    alt="Strategic team placement"
                    height="200px"
                    contain
                  />
                  <v-card-title>Strategic Team Placement</v-card-title>
                  <v-card-text>
                    We don't just find you a contract; we find you a team. We
                    intentionally place our members on healthy, scouted
                    projects, fostering a supportive environment from day one.
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col
                cols="12"
                md="4"
              >
                <v-card class="app-culture-card">
                  <v-img
                    :src="boardgameWide"
                    alt="Team enjoying an outing"
                    height="200px"
                    contain
                  />
                  <v-card-title>Frequent Events & Outings</v-card-title>
                  <v-card-text>
                    Our calendar is packed. From board game nights and happy
                    hours to Top Golf, axe throwing, crab feasts, and our
                    legendary holiday party, we build bonds outside of work.
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col
                cols="12"
                md="4"
              >
                <v-card class="app-culture-card">
                  <v-img
                    :src="mountainWide"
                    alt="Peer mentorship"
                    height="200px"
                    contain
                  />
                  <v-card-title>Organic Peer Mentorship</v-card-title>
                  <v-card-text>
                    Strong communities build strong mentors. Our events and
                    collaborative culture create natural connections, making it
                    easy to ask for help and share expertise.
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- Careers Section -->
    <v-container
      id="careers"
      fluid
      class="py-16"
    >
      <v-row justify="center">
        <v-col
          cols="12"
          md="10"
          class="text-center"
        >
          <div class="app-fade-in-section">
            <h2 class="text-h4 text-sm-h3 text-md-h2 font-weight-bold mb-3 mb-md-4">
              Join Our Team
            </h2>
            <p
              class="text-responsive mx-auto mb-4"
              style="max-width: 700px"
            >
              We're looking for talented engineers to help us solve challenging engineering problems. If you're ready to make an impact,
              we want to hear from you.
            </p>
            <v-btn
              to="/positions"
              color="primary"
              size="large"
              prepend-icon="mdi-briefcase-outline"
              @click="scrollToTop"
            >
              Browse Positions
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- Partners Section -->
    <v-container
      id="partners"
      fluid
      class="py-16"
    >
      <v-row justify="center">
        <TrustedPartners />
      </v-row>
    </v-container>

    <v-footer class="d-flex flex-column">
      <div class="text-center w-100">
        &copy; {{ new Date().getFullYear() }} Yellow Duck Technologies, Inc. All
        Rights Reserved.
      </div>
      <div class="text-center w-100 text-caption">
        Crofton, MD
      </div>
    </v-footer>
  </section>
</template>

<script setup>
import WaveDivider from "@/components/WaveDivider.vue";
import { markRaw, onMounted, computed, ref, onUnmounted } from "vue";
import ImageCarousel from "@/components/ImageCarousel.vue";
import TeamworkSlide from "@/components/TeamworkSlide.vue";
import TalkToUsSlide from "@/components/TalkToUsSlide.vue";
import MentoringSlide from "@/components/MentoringSlide.vue";
import AboutUsSection from "@/components/AboutUsSection.vue";
import TrustedPartners from "@/components/TrustedPartners.vue";
import CompanyPhilosophy from "@/components/CompanyPhilosophy.vue";
import ContactForm from "@/components/ContactForm.vue";
import teamworkImage from "@/assets/duck_kanban_banner.webp";
import teamworkMobileImage from "@/assets/kanban_mobile_.webp";
import talkToUsImage from "@/assets/clean_desk_banner.webp";
import mentoringImage from "@/assets/group_hero.webp";
import mentoringMobileImage from "@/assets/group_mobile.webp";
import coachesWide from "@/assets/coaches_wide.webp";
import boardgameWide from "@/assets/boardgame_wide.webp";
import mountainWide from "@/assets/mountain_wide.webp";


// Reactive screen size detection
const isMobile = ref(false);

const updateScreenSize = () => {
  isMobile.value = window.innerWidth <= 768;
};

// Reactive slides based on screen size
const slides = computed(() => [
  {
    image: isMobile.value ? teamworkMobileImage : teamworkImage,
    alt: "Teamwork",
    component: markRaw(TeamworkSlide),
  },
  {
    image: talkToUsImage,
    alt: "Talk to us",
    component: markRaw(TalkToUsSlide),
  },
  {
    image: isMobile.value ? mentoringMobileImage : mentoringImage,
    alt: "Mentoring",
    component: markRaw(MentoringSlide),
  },
]);



const setupFadeInOnScroll = () => {
  const faders = document.querySelectorAll(".app-fade-in-section");
  const appearOptions = {
    threshold: 0.2,
    rootMargin: "0px 0px -50px 0px",
  };
  const appearOnScroll = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add("visible");
      observer.unobserve(entry.target);
    });
  }, appearOptions);
  faders.forEach((fader) => {
    appearOnScroll.observe(fader);
  });
};

onMounted(() => {
  setupFadeInOnScroll();
  
  // Initialize screen size and set up listener
  updateScreenSize();
  window.addEventListener('resize', updateScreenSize);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateScreenSize);
});

const scrollToTop = () => {
  setTimeout(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, 50);
};
</script>
.v-window__container .fade-enter-active,
.v-window__container .fade-leave-active {
  transition-duration: 1.25s;
}

<style>
@import "../style.css";
</style>

<style scoped>
</style>
